import React from 'react';
import { Shield, Zap, MonitorPlay, ChevronRight } from 'lucide-react';

const MarketingValueProp = () => {
  return (
    <section className="relative py-16 lg:py-20 bg-gradient-to-b from-[#0a0a0a] via-[#1a0808] to-[#0a0a0a]">
      {/* Background red gradient effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#e4002b] rounded-full blur-[200px] opacity-10" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#e4002b] rounded-full blur-[180px] opacity-8" />
      </div>
      
      {/* Main content - same width as other sections */}
      <div className="relative max-w-[1920px] mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1a1a1a]/80 to-[#2a1a1a]/80 border border-white/10 backdrop-blur-sm">
          {/* Inner gradient effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#e4002b] rounded-full blur-[120px] opacity-10" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#e4002b] rounded-full blur-[100px] opacity-10" />
          </div>
          
          {/* 2-column layout */}
          <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-16 items-start p-8 md:p-12 lg:p-16">
            
            {/* Left Column - Text Content */}
            <div className="space-y-5">
              {/* Headline */}
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                El escenario global{' '}
                <span className="text-[#e4002b]">en tu sala</span>
              </h2>
              
              {/* Description */}
              <p className="text-lg text-white font-semibold">
                ¿Por qué conformarte con cine cuando puedes ofrecer un acontecimiento?
              </p>
              <p className="text-lg text-gray-400">
                Transforma tu cine, auditorio o sala municipal en el centro cultural del mundo.
              </p>
              
              {/* Tagline */}
              <div className="pt-2">
                <p className="text-xl lg:text-2xl font-semibold text-white">
                  Tú pones la butaca. <span className="text-[#e4002b]">Nosotros la emoción.</span>
                </p>
                <p className="text-gray-400 mt-2">
                  Rentabiliza tus sesiones. Diversifica tu oferta. Crea comunidad.
                </p>
              </div>
              
              {/* CTAs */}
              <div className="flex flex-wrap gap-4 pt-4">
                <a 
                  href="/catalogo"
                  data-testid="marketing-cta-catalog"
                  className="inline-flex items-center gap-2 bg-[#e4002b] hover:bg-[#b30022] text-white px-6 py-3 font-semibold tracking-wide uppercase text-sm transition-all duration-300 hover:gap-3 rounded-lg"
                >
                  Ver Catálogo
                  <ChevronRight className="w-4 h-4" />
                </a>
                <a 
                  href="/cinema/register"
                  data-testid="marketing-cta-register"
                  className="inline-flex items-center gap-2 border border-white/20 hover:border-[#e4002b] text-white hover:text-[#e4002b] px-6 py-3 font-semibold tracking-wide uppercase text-sm transition-all duration-300 rounded-lg"
                >
                  Regístrate
                </a>
              </div>
            </div>
            
            {/* Right Column - 2x2 Feature Grid */}
            <div className="grid grid-cols-2 gap-4">
              
              {/* Exclusividad - with image */}
              <div className="group relative overflow-hidden rounded-lg border border-white/[0.08] hover:border-[#e4002b]/50 transition-all duration-300">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                  style={{ backgroundImage: `url('https://images.unsplash.com/photo-1580809361436-42a7ec204889?w=600&q=80')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30" />
                <div className="relative p-5 h-full min-h-[180px] flex flex-col justify-end">
                  <h3 className="text-white font-semibold mb-1.5">Exclusividad</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">Royal Opera House de Londres y Filarmónica de Berlín en directo.</p>
                </div>
              </div>

              {/* Garantía */}
              <div className="group p-5 bg-white/[0.03] rounded-lg border border-white/[0.08] hover:border-[#e4002b]/50 transition-all duration-300 min-h-[180px] flex flex-col">
                <Shield className="w-6 h-6 text-[#e4002b] mb-3" />
                <h3 className="text-white font-semibold mb-1.5">Garantía</h3>
                <p className="text-gray-500 text-sm leading-relaxed">15 años liderando el Event Cinema en España.</p>
              </div>

              {/* Plug & Play */}
              <div className="group p-5 bg-white/[0.03] rounded-lg border border-white/[0.08] hover:border-[#e4002b]/50 transition-all duration-300 min-h-[180px] flex flex-col">
                <Zap className="w-6 h-6 text-[#e4002b] mb-3" />
                <h3 className="text-white font-semibold mb-1.5">Plug & Play</h3>
                <p className="text-gray-500 text-sm leading-relaxed">En marcha en 10 minutos. Sin complicaciones.</p>
              </div>

              {/* Calidad Premium - with image */}
              <div className="group relative overflow-hidden rounded-lg border border-white/[0.08] hover:border-[#e4002b]/50 transition-all duration-300">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                  style={{ backgroundImage: `url('https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=600&q=80')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30" />
                <div className="relative p-5 h-full min-h-[180px] flex flex-col justify-end">
                  <h3 className="text-white font-semibold mb-1.5">Calidad Premium</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">Resolución 4K y sonido envolvente 5.1.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketingValueProp;
